import { Controller } from '@hotwired/stimulus';
import gsap from 'gsap';

export default class extends Controller {
  static targets = ['roundNumber', 'roundMedia'];
  static values = { showAnimation: { type: Boolean, default: true }, round: Number, combat: Boolean };

  roundNumberTargetConnected() { this.scheduleRoundAnimation(); }
  roundValueChanged() { this.scheduleRoundAnimation(); }
  showAnimationValueChanged() { this.scheduleRoundAnimation(); }

  combatValueChanged() {
    if (!this.combatValue) {
      this.lastAnimatedRound = undefined;
      this.#cleanupAnimation();
    }
    this.scheduleRoundAnimation();
  }

  scheduleRoundAnimation() {
    if (this.pendingAnimation) return;
    this.pendingAnimation = true;
    // Round attributes can morph before the label/media. Evaluate once after
    // the complete morph; unrelated DOM mutations must not restart a round.
    queueMicrotask(() => {
      this.pendingAnimation = false;
      if (!this.element.isConnected || !this.hasRoundNumberTarget || !this.combatValue || !this.showAnimationValue) return;
      if (this.lastAnimatedRound === this.roundValue) return;
      this.lastAnimatedRound = this.roundValue;
      this.animateRoundNumber();
    });
  }

  roundNumberTargetDisconnected() {
    this.#cleanupAnimation();
  }

  disconnect() {
    this.#cleanupAnimation();
  }

  animateRoundNumber() {
    if (!this.hasRoundNumberTarget || !this.showAnimationValue) return;

    const originalElement = this.roundNumberTarget;
    const overlay = this.element;

    this.#cleanupAnimation();
    this.originalElement = originalElement;
    this.element.roundAnimation = new Promise(resolve => { this.finishRoundAnimation = resolve; });

    // Hide the original element
    originalElement.style.visibility = 'hidden';

    // Create a clone for animation
    const clone = originalElement.cloneNode(true);
    clone.removeAttribute('data-game-screen--round-info-target'); // Remove Stimulus target attribute
    clone.classList.add('game-screen-round-info__round-number--animated');
    // Heartbeats and avatar updates may morph the screen during playback.
    // Keep the presentation until this controller finishes or cancels it.
    clone.id = `${this.element.id}_animation`;
    clone.setAttribute('data-turbo-permanent', '');
    const cloneMedia = clone.querySelector('[data-game-screen--round-info-target~="roundMedia"]');
    cloneMedia?.removeAttribute('data-game-screen--round-info-target');
    overlay.appendChild(clone);
    this.animatedClone = clone;

    // Use the overlay's dimensions (respects rotation)
    const overlayWidth = overlay.offsetWidth;
    const overlayHeight = overlay.offsetHeight;

    // Get the final position of the original element relative to the overlay
    const overlayRect = overlay.getBoundingClientRect();
    const originalRect = originalElement.getBoundingClientRect();

    // Transform viewport coordinates back into overlay's local coordinate space
    // by computing the inverse of the overlay's CSS transform
    const overlayStyle = window.getComputedStyle(overlay);
    const matrix = new DOMMatrix(overlayStyle.transform);
    const inverseMatrix = matrix.inverse();

    // Get original element center in viewport coords, relative to overlay's viewport position
    const vpX = originalRect.left + originalRect.width / 2 - overlayRect.left;
    const vpY = originalRect.top + originalRect.height / 2 - overlayRect.top;

    // Transform to overlay-local coordinates
    const localPoint = inverseMatrix.transformPoint(new DOMPoint(vpX, vpY));
    const finalX = localPoint.x;
    const finalY = localPoint.y;

    // Check if round media is present
    const hasMedia = Boolean(cloneMedia);

    // Calculate font size as 25% of overlay height; halve when media is present
    let fontSize = overlayHeight * (hasMedia ? 0.125 : 0.25);

    // Set initial state - positioned off-screen to the left within the overlay
    gsap.set(clone, {
      position: 'absolute',
      left: '50%',
      top: '50%',
      x: -overlayWidth - 200,
      y: 0,
      xPercent: -50,
      yPercent: -50,
      fontSize: `${fontSize}px`,
      zIndex: 9999,
      fontWeight: 'bold',
      color: 'white',
      textShadow: '0 0 20px rgba(0, 0, 0, 0.8)',
      whiteSpace: 'nowrap',
      visibility: 'visible'
    });

    // Check if text width exceeds overlay width and adjust font size if needed
    const textWidth = clone.offsetWidth;
    const maxWidth = overlayWidth * 0.9; // Use 90% for padding

    if (textWidth > maxWidth) {
      const scale = maxWidth / textWidth;
      fontSize = fontSize * scale;
      gsap.set(clone, {
        fontSize: `${fontSize}px`
      });
    }

    // Show round media if present
    if (cloneMedia) cloneMedia.style.display = 'block';

    // Create animation timeline
    const timeline = (this.timeline = gsap.timeline());

    // Animation sequence - all positions relative to overlay container
    timeline
      // Fly in from left to center of overlay
      .to(clone, {
        x: 0,
        duration: 0.8,
        ease: 'power2.out'
      })
      // First heartbeat - scale up
      .to(clone, {
        scale: 1.2,
        duration: 0.15,
        ease: 'power2.out'
      })
      // First heartbeat - scale back
      .to(clone, {
        scale: 1,
        duration: 0.15,
        ease: 'power2.in'
      })
      // Pause between heartbeats
      .to(clone, {
        duration: 0.1
      })
      // Second heartbeat - scale up
      .to(clone, {
        scale: 1.2,
        duration: 0.15,
        ease: 'power2.out'
      })
      // Second heartbeat - scale back
      .to(clone, {
        scale: 1,
        duration: 0.15,
        ease: 'power2.in'
      })
      // Hold — longer when media is present
      .to(clone, {
        duration: hasMedia ? 5 : 0.5
      })
      // Hide media before flying out
      .call(() => {
        if (cloneMedia) cloneMedia.style.display = 'none';
      })
      // Move to original element's position within the overlay and fade out
      .to(clone, {
        left: `${finalX}px`,
        top: `${finalY}px`,
        xPercent: -50,
        yPercent: -50,
        x: 0,
        fontSize: window.getComputedStyle(originalElement).fontSize,
        opacity: 0,
        duration: 0.5,
        ease: 'power2.inOut',
        onComplete: () => {
          this.#cleanupAnimation();
        }
      });
  }

  #cleanupAnimation() {
    this.timeline?.kill();
    this.timeline = null;
    this.originalElement?.style.removeProperty('visibility');
    this.originalElement = null;
    this.animatedClone?.remove();
    this.animatedClone = null;
    delete this.element.roundAnimation;
    this.finishRoundAnimation?.();
    this.finishRoundAnimation = null;
  }
}
